import React from 'react'

import { Link as RouterLink } from 'react-router-dom'
import { reverse } from 'named-urls'
import { routes } from 'routes'
import moment from 'moment'

import {
  Box,
  Chip,
  Link,
  MenuItem,
  CircularProgress,
  makeStyles,
} from '@material-ui/core'

import {
  TurnedInNotOutlined,
  VerifiedUserOutlined,
  DescriptionOutlined,
  TodayOutlined,
  CalendarTodayOutlined,
  ErrorOutlineOutlined,
} from '@material-ui/icons'

import * as service from 'service'
import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'
import styles from './styles'

import { Container, ContentHeader, ClientInfo, SplitButton } from 'components'
import { ContainerSummary, ItemSummary } from 'components/Summary'
import { Permitted } from 'components/Permissions'
import { LogButton } from 'components/Logs'
import DialogInvoiceChangeDueDate from './components/DialogInvoiceChangeDueDate'
import DialogInvoiceResend from './components/DialogInvoiceResend'
import DialogInvoiceManualConfirmation from './components/DialogInvoiceManualConfirmation'
import DialogInvoiceCancel from './components/DialogInvoiceCancel'
import DialogNFEdit from './components/DialogNFEdit'
import DialogTaxInvoiceCancel from './components/DialogTaxInvoiceCancel'
import InvoiceItems from './components/InvoiceItems'
import Processing from './components/Processing'
import DialogInvoiceReverse from './components/DialogInvoiceReverse'
import usePermissions from 'hooks/usePermissions'

const useStyles = makeStyles(styles)

const InvoicesShow = ({ match }) => {
  const classes = useStyles()

  const invoiceId = match.params.invoiceId
  const permissions = usePermissions()
  const [isLoading, setLoading] = React.useState(true)
  const [invoice, setInvoice] = React.useState({})
  const [customer, setCustomer] = React.useState({})
  const [dialogNfEdit, setDialogNfEdit] = React.useState(false)
  const [
    dialogInvoiceManualConfirmation,
    setDialogInvoiceManualConfirmation,
  ] = React.useState(false)
  const [dialogInvoiceCancel, setDialogInvoiceCancel] = React.useState(false)
  const [dialogInvoiceResend, setDialogInvoiceResend] = React.useState(false)
  const [
    dialogInvoiceChangeDueDate,
    setDialogInvoiceChangeDueDate,
  ] = React.useState(false)
  const [dialogInvoiceReverse, setDialogInvoiceReverse] = React.useState(false)
  const [dialogTaxInvoiceCancel, setDialogTaxInvoiceCancel] = React.useState(
    false,
  )
  const [reload, setReload] = React.useState(false)
  const [pageLoader, setPageLoader] = React.useState(false)
  const snackbar = useSnackbar()

  const {
    notCanceledNorReversed,
    paymentDone,
    isAwaitingPayment,
    isFailedOrCanceled,
    isIssued,
    isFailed,
  } = React.useMemo(() => {
    if (!Object.keys(invoice).length) {
      return {
        notCanceledNorReversed: false,
        paymentDone: false,
        isAwaitingPayment: false,
        isFailedOrCanceled: false,
        isIssued: false,
        isFailed: false,
      }
    }
    return {
      notCanceledNorReversed: helpers.invoices.statusVerification.notCanceledNorReversed(
        invoice.status,
      ),
      paymentDone: helpers.invoices.statusVerification.paymentDone(
        invoice.status,
      ),
      isAwaitingPayment: helpers.invoices.statusVerification.isAwaitingPayment(
        invoice.status,
      ),
      isFailedOrCanceled:
        invoice.serviceTaxInvoice &&
        helpers.taxInvoices.statusVerification.isFailedOrCanceled(
          invoice.serviceTaxInvoice.status,
        ),
      isIssued:
        invoice.serviceTaxInvoice &&
        helpers.taxInvoices.statusVerification.isIssued(
          invoice.serviceTaxInvoice.status,
        ),
      isFailed:
        invoice.serviceTaxInvoice &&
        helpers.taxInvoices.statusVerification.isFailed(
          invoice.serviceTaxInvoice.status,
        ),
    }
  }, [invoice])

  React.useEffect(() => {
    setLoading(true)
    setCustomer({})

    const loadInvoice = async () => {
      let response = await service.recurrence.invoices.get({ invoiceId })
      setInvoice(response.data.invoice)
      return response.data.invoice
    }

    const loadCustomer = async customerId => {
      let response = await service.recurrence.customers.get({
        clientId: customerId,
      })
      setCustomer(response.data.customer)
    }

    loadInvoice()
      .then(invoice => {
        loadCustomer(invoice.customerId)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [invoiceId, reload])

  const openCheckout = () => {
    window.open(invoice.paymentUrl)
  }

  const generateTaxInvoice = async () => {
    setPageLoader(true)
    try {
      await service.recurrence.taxInvoices.create({
        invoiceId: invoice.identifier,
      })

      snackbar.open({
        message: 'Nota fiscal gerada com sucesso!',
        variant: 'success',
      })

      setReload(true)
      setPageLoader(false)
    } catch (exception) {
      setPageLoader(false)

      snackbar.open({
        message: `${exception.response.data.error.message}`,
        variant: 'error',
      })
    }
  }

  const openTaxInvoice = async () => {
    setPageLoader(true)
    setReload(false)

    if (invoice.serviceTaxInvoice.urlInvoice) {
      setPageLoader(false)
      return window.open(invoice.serviceTaxInvoice.urlInvoice, '', 'resizable')
    }

    try {
      const response = await service.recurrence.taxInvoices.download({
        taxInvoiceId: invoice.serviceTaxInvoice.id,
      })
      const fileName = `nf_fatura-#${invoice.identifier}.pdf`
      helpers.archives.downloadPdf(response.data, fileName)
    } catch {
      snackbar.open({
        message: 'Falha ao consultar nota fiscal!',
        variant: 'error',
      })
    } finally {
      setReload(true)
      setPageLoader(false)
    }
  }

  const handleClickVerifyOrders = async () => {
    setPageLoader(true)

    try {
      await service.recurrence.invoices.verifyOrders({ invoiceId })
      await helpers.sleep(15000)

      const response = await service.recurrence.invoices.get({ invoiceId })

      setInvoice(response.data.invoice)
      snackbar.open({
        message: 'Processamentos consultados com sucesso',
        variant: 'success',
      })

      setPageLoader(false)
    } catch (error) {
      snackbar.open({
        message: 'Falha ao consultar processamentos',
        variant: 'error',
      })

      setPageLoader(false)
    }
  }

  return (
    <Container>
      {pageLoader ? (
        <div className={classes.root}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <ContentHeader title="Fatura" subtitle="Detalhes">
            <Box display="flex">
              <>
                {invoice.identifier && (
                  <LogButton
                    to={reverse(routes.invoices.logs, {
                      invoiceIdentifier: invoice.identifier,
                    })}
                  />
                )}

                {(isAwaitingPayment || paymentDone) && (
                  <Box ml={2}>
                    {isAwaitingPayment && (
                      <SplitButton title="Pagamento">
                        <MenuItem
                          key="paymentLink"
                          onClick={() => openCheckout()}
                        >
                          Link de pagamento
                        </MenuItem>

                        {permissions.permitted('confirm_invoice') && (
                          <MenuItem
                            key="manualConfirmation"
                            onClick={() =>
                              setDialogInvoiceManualConfirmation(true)
                            }
                          >
                            Baixa manual
                          </MenuItem>
                        )}

                        {permissions.permitted('update_due_date_invoice') && (
                          <MenuItem
                            onClick={() => setDialogInvoiceChangeDueDate(true)}
                            key="changeDueDate"
                          >
                            Alterar data de vencimento
                          </MenuItem>
                        )}
                        {permissions.permitted('resend_invoice') && (
                          <MenuItem
                            onClick={() => setDialogInvoiceResend(true)}
                            key="resendInvoice"
                          >
                            Reenviar cobrança
                          </MenuItem>
                        )}
                        {permissions.permitted('cancel_invoice') && (
                          <MenuItem
                            onClick={() => setDialogInvoiceCancel(true)}
                            key="cancelInvoice"
                          >
                            Cancelar
                          </MenuItem>
                        )}
                      </SplitButton>
                    )}

                    {paymentDone && (
                      <SplitButton title="Pagamento">
                        {permissions.permitted('reverse_invoice') && (
                          <MenuItem
                            key="reverseInvoice"
                            onClick={() => setDialogInvoiceReverse(true)}
                          >
                            Estornar Fatura
                          </MenuItem>
                        )}
                      </SplitButton>
                    )}
                  </Box>
                )}

                {(notCanceledNorReversed || invoice.serviceTaxInvoice) &&
                  permissions.some([
                    'create_service_tax_invoice',
                    'download_service_tax_invoice',
                    'cancel_service_tax_invoice',
                    'attach_service_tax_invoice',
                  ]) && (
                    <Box ml={2}>
                      <SplitButton title="Nota fiscal">
                        {notCanceledNorReversed &&
                          (!invoice.serviceTaxInvoice || isFailedOrCanceled) &&
                          permissions.permitted(
                            'create_service_tax_invoice',
                          ) && (
                            <MenuItem
                              key="generateTaxInvoice"
                              onClick={() => generateTaxInvoice()}
                            >
                              Gerar
                            </MenuItem>
                          )}
                        {invoice.serviceTaxInvoice &&
                          !isFailed &&
                          permissions.permitted(
                            'download_service_tax_invoice',
                          ) && (
                            <MenuItem
                              key="getTaxInvoice"
                              onClick={() => openTaxInvoice()}
                            >
                              Visualizar
                            </MenuItem>
                          )}
                        {invoice.serviceTaxInvoice &&
                          notCanceledNorReversed &&
                          isIssued &&
                          permissions.permitted(
                            'cancel_service_tax_invoice',
                          ) && (
                            <MenuItem
                              key="cancelTaxInvoice"
                              onClick={() => setDialogTaxInvoiceCancel(true)}
                            >
                              Cancelar
                            </MenuItem>
                          )}
                        {permissions.permitted(
                          'attach_service_tax_invoice',
                        ) && (
                          <MenuItem
                            key="editTaxInvoice"
                            onClick={() => setDialogNfEdit(true)}
                          >
                            Editar
                          </MenuItem>
                        )}
                      </SplitButton>
                    </Box>
                  )}
              </>
            </Box>
          </ContentHeader>
          <Box mb={4}>
            <ContainerSummary isLoading={isLoading}>
              <ItemSummary
                title="Identificador"
                icon={<TurnedInNotOutlined color="disabled" />}
                value={invoice.identifier}
              />
              <ItemSummary
                title="ID no Intermediador"
                icon={<VerifiedUserOutlined color="disabled" />}
                value={invoice.code}
              />
              {invoice?.contractId ? (
                <ItemSummary
                  title="Assinatura"
                  icon={<DescriptionOutlined color="disabled" />}
                  value={
                    <Link
                      component={RouterLink}
                      to={reverse(routes.contracts.show, {
                        contractId: invoice.contractId,
                      })}
                    >
                      {invoice.contractId}
                    </Link>
                  }
                />
              ) : (
                <ItemSummary
                  title="Fatura"
                  icon={<DescriptionOutlined color="disabled" />}
                  value={'Avulsa'}
                />
              )}
              <ItemSummary
                title="Gerada"
                icon={<TodayOutlined color="disabled" />}
                value={moment(invoice.createdAt)
                  .utc(false)
                  .format('DD/MM/YYYY')}
              />
              <ItemSummary
                title="Vencimento"
                icon={<CalendarTodayOutlined color="disabled" />}
                value={moment(invoice.dueDate)
                  .utc(false)
                  .format('DD/MM/YYYY')}
              />
              <ItemSummary
                title="Status"
                icon={<ErrorOutlineOutlined color="disabled" />}
                value={
                  <Chip
                    label={helpers.invoices.status(invoice.status)}
                    size="small"
                    style={{
                      backgroundColor: helpers.invoices.statusColor(
                        invoice.status,
                      ),
                      color: 'white',
                    }}
                  />
                }
              />
            </ContainerSummary>
          </Box>

          <Box mb={4}>
            {!isLoading && customer && <ClientInfo customer={customer} />}
          </Box>

          <Box mb={4}>
            {!isLoading && (
              <InvoiceItems invoice={invoice} setReload={setReload} />
            )}
          </Box>

          <Permitted tag="show_invoice_orders">
            <Box mb={4}>
              {!isLoading && (
                <Processing
                  invoice={invoice}
                  onClickVerifyOrders={handleClickVerifyOrders}
                />
              )}
            </Box>
          </Permitted>

          {!isLoading && (
            <DialogNFEdit
              invoiceId={invoice.identifier}
              open={dialogNfEdit}
              setOpen={setDialogNfEdit}
              onLoading={setPageLoader}
              onEvent={setReload}
              taxInvoice={invoice.serviceTaxInvoice || {}}
            />
          )}
          <DialogInvoiceManualConfirmation
            open={dialogInvoiceManualConfirmation}
            setOpen={setDialogInvoiceManualConfirmation}
            onEvent={setReload}
            invoiceId={invoice.id}
          />
          <DialogInvoiceCancel
            open={dialogInvoiceCancel}
            setOpen={setDialogInvoiceCancel}
            onEvent={setReload}
            invoiceId={invoice.id}
          />
          <DialogInvoiceResend
            open={dialogInvoiceResend}
            setOpen={setDialogInvoiceResend}
            onEvent={setReload}
            invoiceId={invoice.id}
            contractId={invoice.contractId}
          />
          <DialogInvoiceChangeDueDate
            open={dialogInvoiceChangeDueDate}
            setOpen={setDialogInvoiceChangeDueDate}
            onEvent={setReload}
            onLoading={setPageLoader}
            invoiceId={invoice.id}
          />
          <DialogInvoiceReverse
            open={dialogInvoiceReverse}
            setOpen={setDialogInvoiceReverse}
            onLoading={setPageLoader}
            onEvent={setReload}
            invoiceId={invoice.id}
          />
          <DialogTaxInvoiceCancel
            open={dialogTaxInvoiceCancel}
            setOpen={setDialogTaxInvoiceCancel}
            onEvent={setReload}
            onLoading={setPageLoader}
            taxInvoiceId={
              invoice.serviceTaxInvoice && invoice.serviceTaxInvoice.id
            }
          />
        </>
      )}
    </Container>
  )
}

export default InvoicesShow
